import type { NavigateFunction } from 'react-router-dom'
import { LIBERTY_ROLES } from '@/constants/roles'
import {
  ROLE_RENTER,
  type ChangeRoleResponse,
  type UserData,
  type UserRole,
} from '@/types/auth'
import type { AppDispatch } from '@/app/store'
import { changeRole, updateUser } from '@/features/auth/authSlice'
import { fetchApplications, fetchApplicationById } from '@/features/applicant/applicationSlice'
import { verifyUser } from '@/lib/api/userService'
import { clearImpersonationSessionFilters } from '@/lib/auth/impersonationSessionFilters'
import { setStorageItem, STORAGE_KEYS } from '@/lib/utils/storage'
import { consumePostLoginRedirect } from '@/lib/auth/sessionActivity'
import {
  isTabRoleSwitch,
  rememberTabRole,
  shouldResetTabFilters,
} from '@/lib/auth/tabRoleScope'
import { markSecurityNotificationPending } from '@/lib/auth/securityNotificationFlag'
import { resetSecurityCheckSession } from '@/components/SecurityNotificationBanner/securityNotificationCheck'
import { mergeNotificationShownFlags } from '@/features/system-announcement/utils/mergeNotificationShownFlags'
import type { RoleSystemAnnouncement } from '@/features/system-announcement/types/roleNotification'
import { TEMP_USER_ROLE_KEY } from '../constants/sessionKeys'
import type { CompleteRoleChangeResult } from '../types'

/**
 * Angular `login.component.ts` roleSelection — auto-login when current_role is set
 * and is not a Liberty staff role (property/company/renter accounts).
 */
export function shouldAutoLoginWithCurrentRole(userData: UserData): boolean {
  const roleName = userData.current_role?.role_name
  if (!roleName) return false
  return !(LIBERTY_ROLES as readonly string[]).includes(roleName)
}

export function persistUserToStorage(user: UserData): void {
  try {
    localStorage.setItem('user', JSON.stringify(user))
  } catch {
    /* non-fatal */
  }
}

export function setTempUserRole(role: UserRole): void {
  try {
    sessionStorage.setItem(TEMP_USER_ROLE_KEY, JSON.stringify(role))
  } catch {
    /* non-fatal */
  }
}

export function getTempUserRole(): UserRole | null {
  try {
    const raw = sessionStorage.getItem(TEMP_USER_ROLE_KEY)
    return raw ? (JSON.parse(raw) as UserRole) : null
  } catch {
    return null
  }
}

export function clearTempUserRole(): void {
  try {
    sessionStorage.removeItem(TEMP_USER_ROLE_KEY)
  } catch {
    /* non-fatal */
  }
}

export function clearSessionListFilters(): void {
  clearImpersonationSessionFilters()
  try {
    sessionStorage.removeItem('yardisearchguest_filter')
  } catch {
    /* non-fatal */
  }
}

export function enrichRoleFromChangeResponse(
  role: UserRole,
  response: ChangeRoleResponse,
): UserRole {
  const enriched: UserRole = { ...role }
  if (response.notification?.length) {
    // Keep session isshown flags when change_role re-sends the same announcements.
    enriched.notification = mergeNotificationShownFlags(
      response.notification as RoleSystemAnnouncement[] | undefined,
      role.notification as RoleSystemAnnouncement[] | undefined,
    )
  }
  enriched.report_url = response.report_url ?? ''
  if (response.tiles) {
    enriched.tiles = response.tiles.dashboard_tiles ?? (response.tiles as unknown as string[])
  }
  return enriched
}

export function applySelectedRoleToUser(user: UserData, role: UserRole): UserData {
  return { ...user, current_role: role }
}

/**
 * POST /api/change_role — mirrors Angular `userLogin()` / `roleChange()`.
 */
export async function completeRoleChange(
  role: UserRole,
  user: UserData,
  dispatch: AppDispatch,
  otp?: string,
): Promise<CompleteRoleChangeResult> {
  setTempUserRole(role)

  const response = await dispatch(
    changeRole({ role_id: role.role_id, ...(otp ? { otp } : {}) }),
  ).unwrap()

  if (!response.allow_role_change) {
    return { status: 'otp_required', role, response }
  }

  const enrichedRole = enrichRoleFromChangeResponse(role, response)
  if (shouldResetTabFilters(role.role_id)) {
    clearSessionListFilters()
  }
  clearTempUserRole()

  // Angular `roleChange()` → `location.reload()` after setSelectedCurrentUserRole.
  // Refresh from /api/verify so policies, property_listed, org scope, and menu access match the new role.
  let updatedUser: UserData
  try {
    const freshUser = await verifyUser(true)
    const roleFromServer = freshUser.current_role ?? enrichedRole
    // Seed prior notifications (with isshown) from the change_role merge so verify
    // cannot wipe flags the way a raw server current_role would.
    updatedUser = {
      ...freshUser,
      current_role: enrichRoleFromChangeResponse(
        {
          ...roleFromServer,
          notification: enrichedRole.notification ?? roleFromServer.notification,
        },
        response,
      ),
    }
  } catch {
    updatedUser = applySelectedRoleToUser(user, enrichedRole)
  }

  dispatch(updateUser(updatedUser))

  return { status: 'success', role: updatedUser.current_role ?? enrichedRole }
}

/**
 * Navigate after role is confirmed — mirrors Angular renter() vs dashboard redirect.
 * Honours Angular `redirectTo` (consumePostLoginRedirect) when present.
 */
export async function navigateAfterRoleChange(
  role: UserRole,
  navigate: NavigateFunction,
  dispatch: AppDispatch,
): Promise<void> {
  // The saved page belongs to the role that was working in this tab. Entering with a
  // different role starts at that role's landing page instead, and is consumed either
  // way so it cannot resurface later.
  const roleSwitched = isTabRoleSwitch(role.role_id)
  const savedRedirect = consumePostLoginRedirect()
  const resumePath = roleSwitched ? null : savedRedirect
  rememberTabRole(role.role_id)

  if (role.role_name === ROLE_RENTER) {
    setStorageItem(STORAGE_KEYS.userRole, 'applicant')
    if (resumePath) {
      navigate(resumePath, { replace: true })
      return
    }
    try {
      const applications = await dispatch(fetchApplications()).unwrap()
      if (applications.length === 1) {
        const app = applications[0]
        if (app.applicant_id) {
          try {
            await dispatch(fetchApplicationById(app.applicant_id)).unwrap()
          } catch {
            /* continue */
          }
        }
        navigate('/applicant/application/about', { replace: true })
        return
      }
      navigate('/applicant/dashboard', { replace: true })
    } catch {
      navigate('/applicant/dashboard', { replace: true })
    }
    return
  }

  setStorageItem(STORAGE_KEYS.userRole, 'admin')
  // Re-arm when the user finishes role selection and enters the admin shell
  // (avoids toast during /login; ensures flag is still set when dashboard mounts).
  if ((LIBERTY_ROLES as readonly string[]).includes(role.role_name)) {
    resetSecurityCheckSession()
    markSecurityNotificationPending()
  }
  navigate(resumePath || '/dashboard', { replace: true })
}
